/**
 * Offline queue and read cache, on IndexedDB.
 *
 * The operating assumption is that connectivity in a Norwegian fjord is a
 * sometimes-thing. A captain drafting an application at anchor with no signal
 * must be able to finish the form, hit submit, put the phone away, and have it
 * actually arrive when they next pass a mast.
 *
 * Two stores:
 *
 * - **outbox** -- writes waiting to go. Every entry carries a `client_ref`
 *   generated *before* the first attempt, which the server treats as an
 *   idempotency key (`applications_client_ref_key`). That is what makes a retry
 *   after an ambiguous failure safe: if the first attempt actually landed, the
 *   retry resolves to the same application instead of filing a second one.
 *
 * - **cache** -- last-known-good reads, so the map and the applications list
 *   render something on a cold start with no signal rather than a spinner.
 *
 * What is deliberately *not* queued: anchor-down and anchor-up. Those carry a
 * position and a timestamp that mean "here, now". Replaying one an hour later
 * from a queue would file a fix for a place the vessel has left, and the
 * occupancy record is the thing ABG would show a harbourmaster. They fail
 * loudly and the captain retries when they have signal. See `queueable()`.
 */

const DB_NAME = 'abg-anchoring'
const DB_VERSION = 1
const OUTBOX = 'outbox'
const CACHE = 'cache'

export interface OutboxEntry {
  id: string
  path: string
  method: 'POST' | 'PATCH' | 'DELETE'
  body: unknown
  createdAt: number
  attempts: number
  lastError?: string
  /** What to tell the captain this entry is, in the pending list. */
  label: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  return dbPromise
}

async function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(store, mode)
    const request = fn(transaction.objectStore(store))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// ------------------------------------------------------------------- outbox

/** Only writes whose meaning survives being delayed. See the module docstring. */
export function queueable(path: string, method: string): boolean {
  if (method === 'GET') return false
  if (path.includes('/anchor-down') || path.includes('/anchor-up')) return false
  if (path.startsWith('/api/auth/')) return false
  if (path.startsWith('/api/push/')) return false
  return true
}

export function newClientRef(): string {
  return crypto.randomUUID()
}

export async function enqueue(entry: Omit<OutboxEntry, 'createdAt' | 'attempts'>): Promise<void> {
  await tx(OUTBOX, 'readwrite', (store) =>
    store.put({ ...entry, createdAt: Date.now(), attempts: 0 }),
  )
  notify()
  // Ask the service worker to retry the moment the OS says we are back, rather
  // than only when the app happens to be open. Not available on iOS Safari, so
  // flush-on-visibility in App.tsx is the fallback, not the exception.
  void requestBackgroundSync()
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  const all = await tx<OutboxEntry[]>(OUTBOX, 'readonly', (store) => store.getAll())
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

export async function removeFromOutbox(id: string): Promise<void> {
  await tx(OUTBOX, 'readwrite', (store) => store.delete(id))
  notify()
}

async function markAttempt(entry: OutboxEntry, error: string): Promise<void> {
  await tx(OUTBOX, 'readwrite', (store) =>
    store.put({ ...entry, attempts: entry.attempts + 1, lastError: error }),
  )
  notify()
}

export interface FlushResult {
  sent: number
  failed: number
  remaining: number
}

/**
 * Try to drain the outbox. Safe to call as often as you like.
 *
 * A 4xx other than 408/429 is permanent -- the server understood and refused,
 * so retrying forever would just keep a dead entry in the captain's pending
 * list. It is dropped and surfaced. A 5xx or a network failure is transient and
 * stays queued.
 */
export async function flushOutbox(
  send: (entry: OutboxEntry) => Promise<void>,
  onPermanentFailure?: (entry: OutboxEntry, message: string) => void,
): Promise<FlushResult> {
  const entries = await listOutbox()
  let sent = 0
  let failed = 0
  for (const entry of entries) {
    try {
      await send(entry)
      await removeFromOutbox(entry.id)
      sent += 1
    } catch (error) {
      const status = (error as { status?: number }).status
      const message = (error as Error).message ?? 'Unknown error'
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        await removeFromOutbox(entry.id)
        onPermanentFailure?.(entry, message)
        failed += 1
      } else {
        await markAttempt(entry, message)
        // Stop at the first transient failure: the rest will fail the same way
        // and hammering a server that is down or a link that is gone helps
        // nobody, least of all a phone on battery.
        break
      }
    }
  }
  return { sent, failed, remaining: (await listOutbox()).length }
}

async function requestBackgroundSync(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.ready
    // Not in the DOM lib's types, and absent entirely on iOS Safari.
    const sync = (registration as unknown as { sync?: { register(tag: string): Promise<void> } })
      .sync
    await sync?.register('abg-outbox')
  } catch {
    // No Background Sync here. App.tsx flushes on focus and on 'online'.
  }
}

// ------------------------------------------------------------ change events

const listeners = new Set<() => void>()

export function onOutboxChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notify(): void {
  listeners.forEach((fn) => fn())
}

// -------------------------------------------------------------- read cache

export async function cachePut(key: string, value: unknown): Promise<void> {
  await tx(CACHE, 'readwrite', (store) => store.put({ value, at: Date.now() }, key))
}

export async function cacheGet<T>(key: string): Promise<{ value: T; at: number } | null> {
  const row = await tx<{ value: T; at: number } | undefined>(CACHE, 'readonly', (store) =>
    store.get(key),
  )
  return row ?? null
}

/**
 * Fetch, cache on success, fall back to cache on failure.
 *
 * Returns `stale: true` when the value came from cache, so the UI can say so.
 * A captain acting on a two-day-old anchorage list without knowing it is old is
 * worse off than one who sees a spinner.
 */
export async function cached<T>(
  key: string,
  fetcher: () => Promise<T>,
): Promise<{ data: T; stale: boolean; at: number }> {
  try {
    const data = await fetcher()
    await cachePut(key, data)
    return { data, stale: false, at: Date.now() }
  } catch (error) {
    const fallback = await cacheGet<T>(key)
    if (fallback) return { data: fallback.value, stale: true, at: fallback.at }
    throw error
  }
}

export async function clearCache(): Promise<void> {
  await tx(CACHE, 'readwrite', (store) => store.clear())
}
